import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { usePlaylists, useCreatePlaylist, useDeletePlaylist, getPlaylist } from '../../api/playlists.ts';
import { qk } from '../../api/keys.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Loading, ErrorState, EmptyState } from '../../components/ui.tsx';
import { Modal, ConfirmDialog } from '../../components/Modal.tsx';
import { TextField } from '../../components/ui.tsx';
import { ApiError } from '../../api/client.ts';
import { formatRelative, formatDurationWords, plural } from '../../lib/format.ts';
import { PlaylistIcon } from '../../onboarding/icons.tsx';
import { PlaylistPreviewModal } from './PlaylistPreviewModal.tsx';

export function PlaylistsPage() {
  const playlists = usePlaylists();
  const perms = usePermissions();
  const navigate = useNavigate();
  const create = useCreatePlaylist();
  const del = useDeletePlaylist();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; name: string } | null>(null);

  // Item count + total loop duration per row (UX audit P1-4 — the list is
  // "sparse and under-informative" with no sense of what each playlist holds).
  // The editor already computes this per-playlist; reuse the same query here,
  // one per row, so the number is right the first time a playlist is opened
  // (same cache key) instead of only ever appearing inside the editor.
  const summaryQueries = useQueries({
    queries: (playlists.data ?? []).map((p) => ({
      queryKey: qk.playlist(p.id),
      queryFn: () => getPlaylist(p.id),
      staleTime: 30_000,
    })),
  });
  const summaryFor = (playlistId: string) => {
    const idx = (playlists.data ?? []).findIndex((p) => p.id === playlistId);
    const items = summaryQueries[idx]?.data?.items;
    if (!items) return null;
    const totalMs = items.reduce((acc, it) => acc + it.duration_ms, 0);
    return `${items.length} ${plural(items.length, 'item')} · loop ${formatDurationWords(totalMs)}`;
  };

  function submitCreate() {
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: (p) => {
          setCreating(false);
          setName('');
          navigate({ to: '/playlists/$playlistId', params: { playlistId: p.id } });
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
      },
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Playlists</h1>
          <div className="subtitle">Ordered loops of media that a schedule can play.</div>
        </div>
        {perms.canManageContent && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New playlist
          </Button>
        )}
      </div>

      {playlists.isLoading ? (
        <Loading />
      ) : playlists.isError ? (
        <ErrorState message="Could not load playlists." onRetry={() => playlists.refetch()} />
      ) : playlists.data!.length === 0 ? (
        <div className="card card-pad">
          <EmptyState icon={<PlaylistIcon />} title="No playlists yet">
            <p>A playlist is an ordered set of media that loops. Create one, then schedule it.</p>
            {perms.canManageContent && (
              <div className="mt-4">
                <Button variant="primary" onClick={() => setCreating(true)}>
                  New playlist
                </Button>
              </div>
            )}
          </EmptyState>
        </div>
      ) : (
        <div className="table-wrap cards">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Items</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {playlists.data!.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={() => navigate({ to: '/playlists/$playlistId', params: { playlistId: p.id } })}
                >
                  <td className="cell-strong">{p.name}</td>
                  <td className="cell-dim" data-label="Items">
                    {summaryFor(p.id) ?? '—'}
                  </td>
                  <td className="cell-dim" data-label="Updated">
                    {formatRelative(p.updated_at)}
                  </td>
                  <td data-label="" style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <Button size="sm" onClick={() => setPreview({ id: p.id, name: p.name })}>
                        Preview
                      </Button>
                      {perms.canManageContent && (
                        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(p.id)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal
          title="New playlist"
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
              <Button variant="primary" onClick={submitCreate} disabled={!name.trim() || create.isPending}>
                Create
              </Button>
            </>
          }
        >
          <TextField
            label="Playlist name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Promo"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && submitCreate()}
          />
        </Modal>
      )}

      {preview && (
        <PlaylistPreviewModal
          playlistId={preview.id}
          name={preview.name}
          onClose={() => setPreview(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete playlist?"
          message="If a schedule still uses it, deletion is blocked until you remove that schedule."
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            del.mutate(confirmDelete, {
              onSuccess: () => {
                toast.success('Playlist deleted');
                setConfirmDelete(null);
              },
              onError: (e) => {
                setConfirmDelete(null);
                toast.error(e instanceof ApiError ? e.message : 'Failed');
              },
            })
          }
        />
      )}
    </div>
  );
}
